import { resolveCvIdentityVerification } from '@tabbio/utils/cv-identity-verification';
import { useUaePassStatus } from '../../hooks/useUaePassStatus';
import { useContext, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { WebCvRenderer, type WebCvEditSection } from '@tabbio/public-renderers';
import type { CvSectionId } from '@tabbio/types';
import {
  CVDataContext,
  type CVConfig,
  type CVProfile,
  DEFAULT_CV_CONFIG,
  EMPTY_PROFILE,
} from '../../lib/cv-mock-data';
import { useLocale } from '../../hooks/useLocale';
import { UaePassVerificationSheet } from '../uae-pass/UaePassVerificationSheet';

type CvEditScreen = WebCvEditSection;

interface CVDocumentPreviewProps {
  /** Edit handler used by authenticated CV pages. Omit for read-only previews. */
  readOnlyProjects?: boolean;
  onEditSection?: (screen: CvEditScreen) => void;
  /** Optional avatar-specific edit handler used by onboarding previews. */
  onEditPhoto?: () => void;
  /** Optional wrapper style override (CSS). */
  contentStyle?: React.CSSProperties;
  /**
   * Optional override for the CV profile to render. When omitted the component
   * pulls data from `CVDataProvider`. Useful for read-only previews outside
   * the CV edit flow (e.g. employer candidate detail).
   */
  profile?: CVProfile;
  /** Optional override for the CV layout/style config. */
  cvConfig?: CVConfig;
  /**
   * When false, the header block (avatar, name, headline, contact info) is
   * skipped and the preview starts at the profile summary. Useful when the
   * caller already shows the candidate's name/title above the preview.
   * Defaults to true.
   */
  showHeader?: boolean;
  /**
   * Web-only: optional content rendered above the CV header (e.g. page header
   * with share buttons on the `/cv` route). Accepted for parity with the
   * underlying `WebCvRenderer`. Ignored on native.
   */
  topSlot?: ReactNode;
  /** Optional editor guidance rendered inside the document body. */
  bodyLeadSlot?: ReactNode;
  /** Web-only: max width for the rendered CV. Ignored on native. */
  maxWidth?: number;
  /** Web-only preview palette. Authenticated app previews adapt by default. */
  appearance?: 'light' | 'adaptive';
  /**
   * Section include-list + ordering, mirroring
   * `TailoredCVOverrides.sectionsConfig` (skills two-way design). When
   * `order` is present, exactly those sections render in that order: a
   * section id omitted from the list is hidden. When absent/null, the legacy
   * hardcoded order applies. The header always renders first regardless, and
   * content-empty sections are still skipped. Forwarded to `WebCvRenderer`.
   */
  sectionsConfig?: { order: CvSectionId[] } | null;
  /** Authoritative UAE PASS state; only supplied by authenticated app routes. */
  isUaePassVerified?: boolean;
  /**
   * When true (owner's own editable CV), the verified mark becomes the
   * verification entry point: it renders in the owner's own view even when
   * unverified (light gray) and, on tap, opens the shared UAE PASS dialog.
   * Left false for read-only previews (employer/onboarding/share) so those
   * never show a gray badge or a verify CTA.
   */
  enableVerification?: boolean;
}

export function CVDocumentPreview({
  readOnlyProjects = false,
  onEditSection,
  onEditPhoto,
  contentStyle,
  profile: profileOverride,
  cvConfig: cvConfigOverride,
  showHeader = true,
  topSlot,
  bodyLeadSlot,
  maxWidth,
  appearance = 'adaptive',
  sectionsConfig,
  isUaePassVerified = false,
  enableVerification = false,
}: CVDocumentPreviewProps) {
  const { t } = useTranslation('seeker');
  const { isRTL } = useLocale();
  const ctx = useContext(CVDataContext);
  const [verifyOpen, setVerifyOpen] = useState(false);
  // The document preview is an authenticated/private CV surface. Public and
  // token-share routes enforce their visibility policy before constructing the
  // profile override they pass here; applying public-profile privacy again at
  // this layer made the owner's uploaded photo and contact fields disappear.
  const profile: CVProfile = profileOverride ?? ctx?.profile ?? EMPTY_PROFILE;
  const cvConfig: CVConfig = cvConfigOverride ?? ctx?.cvConfig ?? DEFAULT_CV_CONFIG;
  // Verified -> green mark. Unverified -> only shown (light gray) when this is
  // the owner's own editable CV (`enableVerification`), acting as the verify
  // call-to-action. Read-only previews pass nothing so the mark is omitted.
  const ownerPreview = enableVerification || !!onEditSection;
  const { data: identityEvidence } = useUaePassStatus(ownerPreview && isUaePassVerified);
  const verifiedDisplayedName = ownerPreview
    ? resolveCvIdentityVerification({ displayedName: profile.name, isUaePassVerified, uaePassData: identityEvidence?.uaePassData, uaePassLinkedAt: identityEvidence?.linkedAt }).isUaePassVerified
    : isUaePassVerified;
  const verification =
    verifiedDisplayedName || enableVerification
      ? { isVerified: verifiedDisplayedName, label: t('cv.identityVerified') }
      : null;
  return (
    <>
      <WebCvRenderer
        profile={profile}
        config={cvConfig}
        showHeader={showHeader}
        topSlot={topSlot}
        bodyLeadSlot={bodyLeadSlot}
        maxWidth={maxWidth}
        appearance={appearance}
        style={contentStyle}
        readOnlyProjects={readOnlyProjects}
        onEditSection={onEditSection}
        onEditPhoto={onEditPhoto}
        editPhotoLabel={t('cv.edit.changePhoto')}
        isRTL={isRTL}
        sectionsConfig={sectionsConfig}
        viewerMode="self"
        verification={verification}
        onVerifyPress={
          enableVerification ? () => setVerifyOpen(true) : undefined
        }
        labels={{
          profileSummary: t('cv.previewScreen.profileSummary'),
          careerHighlights: t('cv.previewScreen.careerHighlights'),
          keySkills: t('cv.previewScreen.keySkills'),
          professionalExperience: t('cv.previewScreen.professionalExperience'),
          education: t('cv.previewScreen.education'),
          projects: t('cv.previewScreen.projects'),
          certifications: t('cv.previewScreen.certifications'),
          languages: t('cv.previewScreen.languages'),
          verifyCredential: t('cv.previewScreen.verifyCredential'),
        }}
      />
      {enableVerification ? (
        <UaePassVerificationSheet
          visible={verifyOpen}
          onClose={() => setVerifyOpen(false)}
        />
      ) : null}
    </>
  );
}
